import {
  DownOutlined,
  PlusOutlined,
  QuestionCircleOutlined,
  ReloadOutlined,
} from "@ant-design/icons";
import {
  Button,
  DatePicker,
  Dropdown,
  Input,
  InputNumber,
  Modal,
  Select,
  Table,
  Tabs,
  Tag,
  Tooltip,
  Typography,
  message,
} from "antd";
import type { ColumnsType } from "antd/es/table";
import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import {
  Amount,
  StockLink,
  StockPrice,
  ratioToStockPricePct,
  StockProfitRate,
  type StockDetailData,
  CanvasCurveChart,
  type CurveDataPoint,
} from "fin-web-components";
import { queryStockDetailInfoForLongfans } from "../../components/fin-web/stockDetailQuery";
import { useStockLinkDetail } from "../../components/fin-web/useStockLinkDetail";
import StockNoteImportPicker from "./StockNoteImportPicker";
import { SearchStockWithCatalog } from "../../components/fin-web/SearchStockWithCatalog";
import type { StockSearchBrief } from "../../global/GlobalType";
import dayjs from "dayjs";
import customParseFormat from "dayjs/plugin/customParseFormat";
import type { Dayjs } from "dayjs";
import { UserHoldingsApi } from "./UserHoldingsApi";
import type {
  AppUserHoldingLineDTO,
  AppUserTradeHistoryLineDTO,
  ClosedTradeLineDTO,
  MonthlyReviewDetailDTO,
  ProfitCurvePointDTO,
} from "./UserHoldingsType";
import { UserWatchlistApi } from "./UserWatchlistApi";
import type { UserWatchlistGroupDTO, UserWatchlistItemDTO } from "./UserWatchlistType";
import { userHoldingsPageCssVars } from "./Config";
import "./UserHoldingsPage.css";

dayjs.extend(customParseFormat);

const { Text } = Typography;
const RECT_BTN = "user-holdings-rect-modal-btn";

const WATCHLIST_GROUP_NAME_MAX = 6;
const WATCHLIST_GROUP_NAV_GAP = 10;
const ADD_WATCHLIST_GROUP_VALUE = "__uh_add_watchlist_group__";

/** 添加分组弹窗（工具栏 / 添加自选内复用） */
function WatchlistCreateGroupModal(props: {
  open: boolean;
  spaceId: string;
  onClose: () => void;
  onCreated?: (newGroupId: string) => void;
  onAfterListRefresh?: () => void | Promise<void>;
}): JSX.Element {
  const { open, spaceId, onClose, onCreated, onAfterListRefresh } = props;
  const [name, setName] = useState("");
  const [creating, setCreating] = useState(false);

  useEffect(() => {
    if (open) {
      setName("");
    }
  }, [open]);

  const submit = async () => {
    const n = name.trim();
    if (!n) {
      message.warning("请输入分组名称");
      throw new Error("cancel");
    }
    setCreating(true);
    try {
      const r = await UserWatchlistApi.createGroup(spaceId, n);
      if (r?.rows?.length) {
        message.success("已创建分组");
        const sorted = [...r.rows].sort((a, b) => (b.sortOrder ?? 0) - (a.sortOrder ?? 0));
        const newest = sorted[0];
        if (newest?.id && onCreated) {
          onCreated(String(newest.id));
        }
        await onAfterListRefresh?.();
        onClose();
      } else {
        throw new Error("cancel");
      }
    } finally {
      setCreating(false);
    }
  };

  return (
    <Modal
      title="添加分组"
      open={open}
      okText="确认"
      cancelText="取消"
      okButtonProps={{
        className: RECT_BTN,
        style: { width: 120 },
        loading: creating,
      }}
      cancelButtonProps={{ className: RECT_BTN, style: { width: 120 } }}
      onCancel={onClose}
      onOk={() => submit()}
      destroyOnClose
      className="uh-add-watch-modal"
    >
      <Input
        placeholder={`分组名称，最多${WATCHLIST_GROUP_NAME_MAX}个字`}
        maxLength={WATCHLIST_GROUP_NAME_MAX}
        value={name}
        onChange={(e) => setName(e.target.value)}
        className="user-holding-input"
      />
    </Modal>
  );
}

/** 自选分组：横向展示；分组超过 8 个时必显「更多分组」，单行最多 8 个分组按钮 + 更多 */
const WATCHLIST_GROUP_NAV_INLINE_MAX = 8;

function WatchlistGroupsNav(props: {
  groups: UserWatchlistGroupDTO[];
  watchGroupId: string;
  onSelectGroup: (groupId: string) => void;
}): JSX.Element {
  const { groups, watchGroupId, onSelectGroup } = props;
  const growRef = useRef<HTMLDivElement>(null);
  const measureWrapRef = useRef<HTMLDivElement>(null);
  const [containerW, setContainerW] = useState(0);
  const [widths, setWidths] = useState<number[]>([]);
  const [moreW, setMoreW] = useState(56);
  const [moreOpen, setMoreOpen] = useState(false);

  const groupKey = useMemo(() => groups.map((g) => String(g.id ?? "")).join("|"), [groups]);

  useLayoutEffect(() => {
    const el = growRef.current;
    if (!el) {
      return;
    }
    const ro = new ResizeObserver((entries) => {
      const w = entries[0]?.contentRect?.width ?? el.clientWidth;
      setContainerW(w);
    });
    ro.observe(el);
    setContainerW(el.clientWidth);
    return () => ro.disconnect();
  }, []);

  useLayoutEffect(() => {
    const wrap = measureWrapRef.current;
    if (!wrap || groups.length === 0) {
      setWidths([]);
      return;
    }
    const btns = wrap.querySelectorAll<HTMLElement>("[data-measure-group]");
    const arr = Array.from(btns).map((b) => b.offsetWidth);
    setWidths(arr);
  }, [groupKey, groups]);

  const layout = useMemo(() => {
    const n = groups.length;
    const gap = WATCHLIST_GROUP_NAV_GAP;
    if (n === 0 || widths.length !== n) {
      return { mode: "all" as const, k: n };
    }
    if (containerW <= 0) {
      if (n > WATCHLIST_GROUP_NAV_INLINE_MAX) {
        return {
          mode: "trunc" as const,
          k: Math.min(WATCHLIST_GROUP_NAV_INLINE_MAX, n - 1),
        };
      }
      return { mode: "all" as const, k: n };
    }
    const sumAll = widths.reduce((a, b) => a + b, 0) + Math.max(0, n - 1) * gap;
    if (n <= WATCHLIST_GROUP_NAV_INLINE_MAX && sumAll <= containerW) {
      return { mode: "all" as const, k: n };
    }
    const maxK =
      n > WATCHLIST_GROUP_NAV_INLINE_MAX
        ? Math.min(WATCHLIST_GROUP_NAV_INLINE_MAX, n - 1)
        : n - 1;
    for (let k = maxK; k >= 0; k--) {
      const prefix = k === 0 ? 0 : widths.slice(0, k).reduce((a, b) => a + b, 0);
      const gaps = k === 0 ? 0 : k * gap;
      const total = prefix + gaps + moreW;
      if (total <= containerW) {
        return { mode: "trunc" as const, k };
      }
    }
    return { mode: "trunc" as const, k: 0 };
  }, [groups.length, widths, containerW, moreW]);

  const selectedIndex = useMemo(() => {
    const i = groups.findIndex((g) => String(g.id ?? "") === watchGroupId);
    return i >= 0 ? i : -1;
  }, [groups, watchGroupId]);

  const moreTriggerLabel = useMemo(() => {
    if (layout.mode !== "trunc") {
      return "更多分组";
    }
    if (selectedIndex < 0) {
      return "更多分组";
    }
    if (selectedIndex >= layout.k) {
      return String(groups[selectedIndex]?.groupName ?? "—");
    }
    return "更多分组";
  }, [layout.mode, layout.k, selectedIndex, groups]);

  useLayoutEffect(() => {
    const wrap = measureWrapRef.current;
    if (!wrap || groups.length === 0) {
      return;
    }
    const m = wrap.querySelector<HTMLElement>("[data-measure-more]");
    setMoreW(m?.offsetWidth ?? 56);
  }, [groupKey, groups, moreTriggerLabel]);

  const moreActive = layout.mode === "trunc" && selectedIndex >= layout.k;

  if (groups.length === 0) {
    return <span className="uh-watchlist-empty-hint">暂无分组</span>;
  }

  const renderGroupBtn = (g: UserWatchlistGroupDTO, idx: number) => {
    const gid = String(g.id ?? "");
    const gname = String(g.groupName ?? "—");
    const on = watchGroupId === gid;
    return (
      <button
        key={g.id ?? `g-${idx}`}
        type="button"
        className={
          on
            ? "uh-watchlist-group-text-btn uh-watchlist-group-text-btn--on"
            : "uh-watchlist-group-text-btn"
        }
        onClick={() => {
          onSelectGroup(gid);
          setMoreOpen(false);
        }}
      >
        {gname}
      </button>
    );
  };

  const morePanel = (
    <div className="uh-watchlist-more-panel" role="listbox">
      {groups.map((g, idx) => {
        const gid = String(g.id ?? "");
        const gname = String(g.groupName ?? "—");
        const on = watchGroupId === gid;
        return (
          <button
            key={g.id ?? `m-${idx}`}
            type="button"
            role="option"
            className={
              on ? "uh-watchlist-more-cell uh-watchlist-more-cell--on" : "uh-watchlist-more-cell"
            }
            onClick={() => {
              onSelectGroup(gid);
              setMoreOpen(false);
            }}
            title={gname}
          >
            <span className="uh-watchlist-more-cell-text">{gname}</span>
          </button>
        );
      })}
    </div>
  );

  return (
    <div className="uh-watchlist-groups-nav-root">
      <div ref={growRef} className="uh-watchlist-groups-inline">
        {layout.mode === "all" && groups.map((g, i) => renderGroupBtn(g, i))}
        {layout.mode === "trunc" && (
          <>
            {groups.slice(0, layout.k).map((g, i) => renderGroupBtn(g, i))}
            <Dropdown
              trigger={["click"]}
              open={moreOpen}
              onOpenChange={setMoreOpen}
              popupRender={() => morePanel}
              overlayStyle={{ padding: 0, background: "transparent", boxShadow: "none" }}
            >
              <span className="uh-watchlist-more-dropdown-anchor">
                <button
                  type="button"
                  className={
                    moreActive
                      ? "uh-watchlist-group-text-btn uh-watchlist-group-text-btn--on uh-watchlist-more-trigger"
                      : "uh-watchlist-group-text-btn uh-watchlist-more-trigger"
                  }
                >
                  <span className="uh-watchlist-more-label">{moreTriggerLabel}</span>
                  <DownOutlined className="uh-watchlist-more-caret" />
                </button>
              </span>
            </Dropdown>
          </>
        )}
      </div>
      <div
        ref={measureWrapRef}
        className="uh-watchlist-measure-strip"
        aria-hidden="true"
      >
        {groups.map((g, idx) => (
          <button
            key={`mes-${g.id ?? idx}`}
            type="button"
            data-measure-group
            className="uh-watchlist-group-text-btn"
            tabIndex={-1}
          >
            {String(g.groupName ?? "—")}
          </button>
        ))}
        <button
          type="button"
          data-measure-more
          className="uh-watchlist-group-text-btn uh-watchlist-more-trigger"
          tabIndex={-1}
        >
          <span className="uh-watchlist-more-label">{moreTriggerLabel}</span>
          <DownOutlined className="uh-watchlist-more-caret" />
        </button>
      </div>
    </div>
  );
}

interface UserHoldingsPageProps {
  spaceId: string;
}

const REASON_OPTS = ["技术面", "消息面", "基本面"];

function ReasonDetailWithImport(props: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  maxLength?: number;
  rows?: number;
  placeholder?: string;
  stockCode?: string;
  onImportClick?: () => void;
}): JSX.Element {
  const {
    label,
    value,
    onChange,
    maxLength = 300,
    rows = 4,
    placeholder,
    stockCode,
    onImportClick,
  } = props;
  return (
    <div className="user-holding-form-block">
      <div className="uh-reason-detail-head">
        <span className="user-holding-lab">{label}</span>
        {stockCode && onImportClick ? (
          <button type="button" className="uh-reason-import-btn" onClick={onImportClick}>
            从笔记中导入
          </button>
        ) : null}
      </div>
      <Input.TextArea
        placeholder={placeholder}
        maxLength={maxLength}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        rows={rows}
        className="user-holding-input"
      />
    </div>
  );
}

function formatCnMin(isoLike: unknown): string {
  if (isoLike == null) {
    return "—";
  }
  const s = String(isoLike).trim();
  if (!s) {
    return "—";
  }
  const d = dayjs(s, "YYYY-MM-DD HH:mm:ss");
  if (!d.isValid()) {
    return s;
  }
  return d.format("YYYY年MM月DD日 HH:mm");
}

/** 统计区间等到日：YYYY年MM月DD日（忽略时分） */
function formatCnDateOnly(isoLike: unknown): string {
  if (isoLike == null) {
    return "—";
  }
  const s = String(isoLike).trim();
  if (!s) {
    return "—";
  }
  const d = dayjs(s, ["YYYY-MM-DD HH:mm:ss", "YYYY-MM-DD HH:mm:ss.SSS", "YYYY-MM-DD"], true);
  if (d.isValid()) {
    return d.format("YYYY年MM月DD日");
  }
  const fallback = dayjs(s);
  if (!fallback.isValid()) {
    return s;
  }
  return fallback.format("YYYY年MM月DD日");
}

/** 接口日期字段经 `convertDates` 可能为 Date 或字符串，便于按日前缀比较 */
function holdingsApiDateTimeString(v: unknown): string {
  if (v == null || v === "") {
    return "";
  }
  if (typeof v === "string") {
    return v.trim();
  }
  if (v instanceof Date) {
    return dayjs(v).format("YYYY-MM-DD HH:mm:ss");
  }
  return String(v).trim();
}

/** 与 Mock / 领域约定一致：`类型 : 具体原因`（买入理由、交易操作理由） */
function parseBuyReasonDisplay(s: string): { type: string; detail: string } {
  const trySplit = (sep: string): { type: string; detail: string } | null => {
    const i = s.indexOf(sep);
    if (i < 0) {
      return null;
    }
    const type = s.slice(0, i).trim();
    const detail = s.slice(i + sep.length).trim();
    if (type && detail) {
      return { type, detail };
    }
    return null;
  };
  const order = [" : ", "：", ": "] as const;
  for (const sep of order) {
    const r = trySplit(sep);
    if (r) {
      return r;
    }
  }
  const j = s.indexOf(":");
  if (j >= 0) {
    const type = s.slice(0, j).trim();
    const detail = s.slice(j + 1).trim();
    if (type && detail) {
      return { type, detail };
    }
  }
  return { type: "", detail: s };
}

interface ReviewSegment {
  motivation?: string;
  tradeCount?: number;
  effectiveCount?: number;
  note?: string;
}

function parseReviewSegments(json?: string | null): ReviewSegment[] {
  try {
    const chart = json != null && json !== "" ? (JSON.parse(json) as { segments?: ReviewSegment[] }) : {};
    return Array.isArray(chart.segments) ? chart.segments : [];
  } catch {
    return [];
  }
}

/** 复盘详情（偏苹果式排版：层级、留白、系统色） */
function MonthlyReviewDetailView(props: { detail: MonthlyReviewDetailDTO | null }): JSX.Element | null {
  const { detail } = props;
  if (!detail?.id) {
    return null;
  }
  const segs = parseReviewSegments(detail.motivationChartJson);
  const bodyParas = String(detail.bodyText ?? "")
    .split(/\n\n+/)
    .map((p) => p.trim())
    .filter(Boolean);
  const havePeriod =
    String(detail.periodStartAt ?? "").trim() !== "" && String(detail.periodEndAt ?? "").trim() !== "";
  const periodLabel = havePeriod
    ? `${formatCnDateOnly(detail.periodStartAt)} — ${formatCnDateOnly(detail.periodEndAt)}`
    : "—";

  return (
    <div className="uh-ai-detail-root">
      <div className="uh-ai-sticky-head">
        <h2 className="uh-ai-hero">{detail.title ?? "—"}</h2>
        <p className="uh-ai-period">{periodLabel}</p>
      </div>
      <div className="uh-ai-scroll-body">
        {detail.summary != null && String(detail.summary).trim() !== "" ? (
          <div className="uh-ai-lead-card">{String(detail.summary).trim()}</div>
        ) : null}
        {bodyParas.length > 0 ? (
          <div className="uh-ai-article">
            {bodyParas.map((para, idx) => (
              <p key={idx} className="uh-ai-para">
                {para}
              </p>
            ))}
          </div>
        ) : null}
        {segs.length > 0 ? (
          <div className="uh-ai-chart-card">
            <p className="uh-ai-section-label">动机分布</p>
            <div className="uh-ai-bars">
              {segs.map((s, i) => {
                const trade = typeof s.tradeCount === "number" ? s.tradeCount : 0;
                const eff = typeof s.effectiveCount === "number" ? s.effectiveCount : 0;
                const pct = trade > 0 ? Math.min(100, Math.round((eff / trade) * 1000) / 10) : 0;
                const name = String(s.motivation ?? "—");
                return (
                  <div key={`${name}-${i}`} className="uh-ai-bar-row">
                    <div className="uh-ai-bar-head">
                      <span className="uh-ai-bar-name">{name}</span>
                      <span className="uh-ai-bar-meta">
                        有效 {eff} / {trade}
                      </span>
                    </div>
                    <div className="uh-ai-bar-track">
                      <div className="uh-ai-bar-fill" style={{ width: `${String(pct)}%` }} />
                    </div>
                    {s.note != null && String(s.note).trim() !== "" ? (
                      <p className="uh-ai-bar-note">{String(s.note).trim()}</p>
                    ) : null}
                  </div>
                );
              })}
            </div>
          </div>
        ) : null}
      </div>
    </div>
  );
}

const stockDetailPromiseCache = new Map<string, Promise<StockDetailData | null>>();

function useStockDetailCached(code: string): StockDetailData | null | undefined {
  const trimmed = code.trim();
  const [d, setD] = useState<StockDetailData | null | undefined>(undefined);
  useEffect(() => {
    if (!trimmed) {
      setD(null);
      return;
    }
    let p = stockDetailPromiseCache.get(trimmed);
    if (!p) {
      p = queryStockDetailInfoForLongfans(trimmed);
      stockDetailPromiseCache.set(trimmed, p);
    }
    void p.then((x) => setD(x ?? null));
  }, [trimmed]);
  return d;
}

function WatchlistFundamentalScoreCell(props: { stockCode?: string }): JSX.Element {
  const d = useStockDetailCached(String(props.stockCode ?? ""));
  if (d === undefined) {
    return <span className="user-holding-sub">加载中…</span>;
  }
  if (!d) {
    return <span className="user-holding-sub">—</span>;
  }
  const v = d.performanceScore;
  return <span>{typeof v === "number" && Number.isFinite(v) ? v.toFixed(1) : "—"}</span>;
}

function WatchlistPositionScoreCell(props: { stockCode?: string }): JSX.Element {
  const d = useStockDetailCached(String(props.stockCode ?? ""));
  if (d === undefined) {
    return <span className="user-holding-sub">加载中…</span>;
  }
  if (!d) {
    return <span className="user-holding-sub">—</span>;
  }
  const v = d.positionScore;
  return <span>{typeof v === "number" && Number.isFinite(v) ? v.toFixed(1) : "—"}</span>;
}

function WatchlistRowPriceCell(props: { stockCode: string }): JSX.Element {
  const d = useStockDetailCached(props.stockCode);
  if (d === undefined) {
    return <span className="user-holding-sub">加载中…</span>;
  }
  if (!d) {
    return <span className="user-holding-sub">—</span>;
  }
  return (
    <StockPrice
      mode="one-line"
      price={d.latestPrice}
      change={ratioToStockPricePct(d.priceChange)}
    />
  );
}

/** 理由展示：类型为浅蓝标签 + 详情单行省略，点击 Tooltip 看全文（买入理由、操作理由同格式） */
function BuyReasonCell({ text }: { text?: string | null }): JSX.Element {
  const raw = text != null && String(text).trim() !== "" ? String(text).trim() : "";
  if (!raw) {
    return <span className="user-holding-sub">—</span>;
  }
  const { type, detail } = parseBuyReasonDisplay(raw);
  const body = (
    <div className="user-holding-reason-detail-wrap">
      <Tooltip
        title={<div className="user-holding-reason-tooltip-body">{raw}</div>}
        trigger={["click"]}
        overlayInnerStyle={{ maxWidth: 420 }}
      >
        <span className="user-holding-reason-ellipsis">{detail}</span>
      </Tooltip>
    </div>
  );
  return (
    <div className="user-holding-buy-reason-row">
      {type ? <Tag className="user-holding-reason-type-tag">{type}</Tag> : null}
      {body}
    </div>
  );
}

export default function UserHoldingsPage(props: UserHoldingsPageProps): JSX.Element {
  const { spaceId } = props;
  /** 当前激活的 Tab：holdings | closed | history | watchlist */
  const [activeTab, setActiveTab] = useState<"holdings" | "closed" | "history" | "watchlist">(
    "holdings"
  );
  /** 接口一次性返回：全部持仓中 + 180 天内结仓；Tab 仅前端筛选 */
  const [allHoldings, setAllHoldings] = useState<AppUserHoldingLineDTO[]>([]);
  const [loading, setLoading] = useState(false);
  const [curve, setCurve] = useState<ProfitCurvePointDTO[]>([]);
  const [hist, setHist] = useState<AppUserTradeHistoryLineDTO[]>([]);
  const [histLoading, setHistLoading] = useState(false);
  const [histPage, setHistPage] = useState(1);

  // 已完结列表数据
  const [closedTrades, setClosedTrades] = useState<ClosedTradeLineDTO[]>([]);
  const [closedLoading, setClosedLoading] = useState(false);
  const [closedPage, setClosedPage] = useState(1);

  // 前端分页
  const [holdingsPage, setHoldingsPage] = useState(1);
  const HOLDINGS_PAGE_SIZE = 10;

  // 收益曲线弹框
  const [dailyProfitModalOpen, setDailyProfitModalOpen] = useState(false);
  const [closedProfitModalOpen, setClosedProfitModalOpen] = useState(false);

  const [openBuyModal, setOpenBuyModal] = useState(false);
  const [openBuySeed, setOpenBuySeed] = useState<StockSearchBrief | null>(null);
  const [addModal, setAddModal] = useState<AppUserHoldingLineDTO | null>(null);
  const [sellModal, setSellModal] = useState<AppUserHoldingLineDTO | null>(null);
  const [aiOpen, setAiOpen] = useState(false);

  const [watchGroups, setWatchGroups] = useState<UserWatchlistGroupDTO[]>([]);
  const [watchGroupId, setWatchGroupId] = useState<string>("");
  const [watchItems, setWatchItems] = useState<UserWatchlistItemDTO[]>([]);
  const [watchLoading, setWatchLoading] = useState(false);
  const [addWatchOpen, setAddWatchOpen] = useState(false);
  const [watchAddGroupOpen, setWatchAddGroupOpen] = useState(false);
  const [watchManageOpen, setWatchManageOpen] = useState(false);
  const {
    stockLinkDetail,
    requestStockDetailForLink,
    buildStockNotesTabProps,
    loadPanel,
    listNotesForImport,
    notesEntitled,
  } = useStockLinkDetail(spaceId);
  const [noteImport, setNoteImport] = useState<{
    stockCode: string;
    stockName: string;
    onPick: (content: string) => void;
  } | null>(null);
  const [writeNoteTarget, setWriteNoteTarget] = useState<{
    code: string;
    name: string;
  } | null>(null);

  const openNoteImport = useCallback(
    (stockCode: string, stockName: string, onPick: (content: string) => void) => {
      if (!notesEntitled) {
        return;
      }
      void loadPanel(stockCode);
      setNoteImport({ stockCode, stockName, onPick });
    },
    [loadPanel, notesEntitled]
  );

  const noteImportHandler = notesEntitled ? openNoteImport : undefined;

  const loadList = useCallback(async () => {
    setLoading(true);
    try {
      const data = await UserHoldingsApi.listHoldings(spaceId);
      setAllHoldings(data != null && Array.isArray(data.rows) ? data.rows : []);
    } finally {
      setLoading(false);
    }
  }, [spaceId]);

  // 加载已完结列表
  const loadClosedTrades = useCallback(async () => {
    setClosedLoading(true);
    try {
      const data = await UserHoldingsApi.listClosedTrades(spaceId);
      setClosedTrades(data != null && Array.isArray(data.rows) ? data.rows : []);
    } finally {
      setClosedLoading(false);
    }
  }, [spaceId]);

  /** 我的持仓 Tab 仅展示持仓中 */
  const openHoldingsRows = useMemo(
    () => allHoldings.filter((r) => r.holdingStatus === "OPEN"),
    [allHoldings]
  );

  const loadCharts = useCallback(async () => {
    const c = await UserHoldingsApi.listProfitCurve(spaceId);
    setCurve(c?.points ?? []);
  }, [spaceId]);

  const loadHist = useCallback(async () => {
    setHistLoading(true);
    try {
      const h = await UserHoldingsApi.listTradeHistory(spaceId);
      setHist(h?.rows ?? []);
    } finally {
      setHistLoading(false);
    }
  }, [spaceId]);

  const loadWatchGroups = useCallback(async () => {
    const g = await UserWatchlistApi.listGroups(spaceId);
    const rows = g?.rows ?? [];
    setWatchGroups(rows);
    setWatchGroupId((prev) => {
      if (prev && rows.some((x) => String(x.id ?? "") === prev)) {
        return prev;
      }
      return "";
    });
  }, [spaceId]);

  const loadWatchItems = useCallback(async () => {
    if (!watchGroupId) {
      setWatchItems([]);
      return;
    }
    setWatchLoading(true);
    try {
      const r = await UserWatchlistApi.listItems(spaceId, watchGroupId);
      setWatchItems(r?.rows ?? []);
    } finally {
      setWatchLoading(false);
    }
  }, [spaceId, watchGroupId]);

  useEffect(() => {
    void loadList();
  }, [loadList]);

  useEffect(() => {
    void loadCharts();
    void loadHist();
  }, [loadCharts, loadHist]);

  // Tab 切换时加载对应数据
  useEffect(() => {
    if (activeTab === "closed") {
      void loadClosedTrades();
    } else if (activeTab === "history") {
      void loadHist();
    } else if (activeTab === "watchlist") {
      void loadWatchGroups();
    }
  }, [activeTab, loadClosedTrades, loadHist, loadWatchGroups]);

  useEffect(() => {
    if (activeTab !== "watchlist") {
      return;
    }
    void loadWatchItems();
  }, [activeTab, loadWatchItems, watchGroupId]);

  const columns: ColumnsType<AppUserHoldingLineDTO> = useMemo(
    () => [
      {
        title: "股票",
        align: "left",
        width: 180,
        render: (_: unknown, r) => {
          const code = String(r.stockCode ?? "").trim();
          const name = String(r.stockName ?? "—");
          if (!code) {
            return <span className="user-holding-tight">{name}</span>;
          }
          return (
            <StockLink
              stockCode={code}
              stockName={name}
              stockDetail={stockLinkDetail.code === code ? stockLinkDetail.data : null}
              detailLoading={stockLinkDetail.code === code && stockLinkDetail.loading}
              dayKlines={stockLinkDetail.code === code ? stockLinkDetail.dayKlines : null}
              klineLoading={stockLinkDetail.code === code && stockLinkDetail.klineLoading}
              onRequestDetail={requestStockDetailForLink}
              stockNotesTab={buildStockNotesTabProps(code, name)}
            />
          );
        },
      },
      {
        title: "最新价格",
        align: "center",
        width: 120,
        render: (_: unknown, r) =>
          typeof r.latestPrice === "number" && typeof r.todayChangePct === "number" ? (
            <StockPrice
              mode="two-line"
              price={r.latestPrice}
              change={ratioToStockPricePct(r.todayChangePct)}
            />
          ) : (
            <span className="user-holding-sub">—</span>
          ),
      },
      {
        title: "买入",
        align: "center",
        width: 168,
        render: (_: unknown, r) => (
          <div>
            <Amount value={r.buyPrice} />
            <div className="user-holding-sub">{formatCnMin(r.buyTime)}</div>
          </div>
        ),
      },
      {
        title: "买入股数",
        align: "center",
        width: 88,
        dataIndex: "buyShares",
      },
      {
        title: "买入理由",
        align: "left",
        width: 220,
        onCell: () => ({ className: "user-holding-reason-td" }),
        render: (_: unknown, r) => <BuyReasonCell text={r.buyReasonDisplay} />,
      },
      {
        title: "卖出价/状态",
        align: "center",
        width: 168,
        render: (_: unknown, r) =>
          r.holdingStatus === "SOLD" ? (
            <div>
              {typeof r.sellPrice === "number" ? (
                <Amount value={r.sellPrice} />
              ) : (
                <span className="user-holding-sub">—</span>
              )}
              <div className="user-holding-sub">{formatCnMin(r.sellTime)}</div>
            </div>
          ) : (
            <span className="user-holding-st-open">持仓中</span>
          ),
      },
      {
        title: "持仓收益",
        align: "center",
        width: 120,
        render: (_: unknown, r) => (
          <StockProfitRate
            value={r.positionProfitAmount ?? null}
            rate={
              typeof r.positionProfitRatio === "number" && Number.isFinite(r.positionProfitRatio)
                ? r.positionProfitRatio
                : null
            }
            lines={2}
          />
        ),
      },
     
      {
        title: "操作",
        align: "center",
        fixed: "right",
        width: 140,
        render: (_: unknown, r) => (
          <>
            <Button type="link" size="small" disabled={r.holdingStatus !== "OPEN"} onClick={() => setSellModal(r)}>
              卖出
            </Button>
            <Button type="link" size="small" disabled={r.holdingStatus !== "OPEN"} onClick={() => setAddModal(r)}>
              加仓
            </Button>
          </>
        ),
      },
    ],
    [stockLinkDetail, requestStockDetailForLink, buildStockNotesTabProps]
  );

  // 计算今日收益：当前持仓 + 今日卖出的股票整体盈亏
  const todayProfit = useMemo(() => {
    const todayStr = dayjs().format("YYYY-MM-DD");
    let profitAmount = 0;
    let costAmount = 0;

    allHoldings.forEach((r) => {
      if (r.holdingStatus === "OPEN" && typeof r.positionProfitAmount === "number") {
        profitAmount += r.positionProfitAmount;
        if (typeof r.buyPrice === "number" && typeof r.buyShares === "number") {
          costAmount += r.buyPrice * r.buyShares;
        }
      }
      // 今日卖出的股票（sellTime 可能为 Date）
      const sellTimeStr = holdingsApiDateTimeString(r.sellTime);
      if (r.holdingStatus === "SOLD" && sellTimeStr.startsWith(todayStr)) {
        if (typeof r.positionProfitAmount === "number") {
          profitAmount += r.positionProfitAmount;
        }
        if (typeof r.buyPrice === "number" && typeof r.buyShares === "number") {
          costAmount += r.buyPrice * r.buyShares;
        }
      }
    });

    const profitRate = costAmount > 0 ? (profitAmount / costAmount) * 100 : 0;
    return { amount: profitAmount, rate: profitRate };
  }, [allHoldings]);

  // 计算完结收益：所有完结的股票对应的盈亏和比例（盈亏/成本）
  const closedProfit = useMemo(() => {
    let profitAmount = 0;
    let costAmount = 0;

    allHoldings.forEach((r) => {
      if (r.holdingStatus === "SOLD") {
        // 买入成本
        if (typeof r.buyPrice === "number" && typeof r.buyShares === "number") {
          const buyCost = r.buyPrice * r.buyShares;
          costAmount += buyCost;
          // 卖出收益 = 卖出价 * 股数 - 买入成本
          if (typeof r.sellPrice === "number") {
            const sellRevenue = r.sellPrice * r.buyShares;
            profitAmount += sellRevenue - buyCost;
          }
        }
      }
    });

    // 盈亏/成本，保留2位有效数字
    const profitRate = costAmount > 0 ? Number(((profitAmount / costAmount) * 100).toFixed(2)) : 0;
    return { amount: profitAmount, rate: profitRate };
  }, [allHoldings]);

  /** 完结胜率：所有已完结明细中，单票收益率≥0的笔数 / 完结笔数（与完结收益的口径一致时可优先用成交价推算） */
  const closedWinRatePct = useMemo(() => {
    const soldRows = allHoldings.filter((r) => r.holdingStatus === "SOLD");
    const total = soldRows.length;
    if (total === 0) {
      return null;
    }
    const isNonLoss = (r: AppUserHoldingLineDTO): boolean => {
      const buyPx = r.buyPrice;
      const shares = r.buyShares;
      const sellPx = r.sellPrice;
      // 与 closedProfit：按买入价×股数、卖出价×股数推算单笔盈亏
      if (typeof buyPx === "number" && typeof shares === "number") {
        const buyCost = buyPx * shares;
        if (typeof sellPx === "number") {
          const profit = sellPx * shares - buyCost;
          return profit >= 0;
        }
      }
      if (typeof r.positionProfitRatio === "number" && Number.isFinite(r.positionProfitRatio)) {
        return r.positionProfitRatio >= 0;
      }
      if (typeof r.positionProfitAmount === "number" && Number.isFinite(r.positionProfitAmount)) {
        return r.positionProfitAmount >= 0;
      }
      return false;
    };
    const wins = soldRows.filter(isNonLoss).length;
    return Number(((wins / total) * 100).toFixed(2));
  }, [allHoldings]);

  // 持仓列表前端分页
  const holdingsPageSlice = useMemo(() => {
    return openHoldingsRows.slice(
      (holdingsPage - 1) * HOLDINGS_PAGE_SIZE,
      holdingsPage * HOLDINGS_PAGE_SIZE
    );
  }, [openHoldingsRows, holdingsPage]);

  const reloadCurrentTabData = useCallback(() => {
    if (activeTab === "holdings") {
      void loadList();
    } else if (activeTab === "closed") {
      void loadClosedTrades();
    } else if (activeTab === "watchlist") {
      void loadWatchGroups();
      void loadWatchItems();
    } else {
      void loadHist();
    }
  }, [activeTab, loadList, loadClosedTrades, loadHist, loadWatchGroups, loadWatchItems]);

  const tabBarMetricsExtra = useMemo(() => {
    const refreshSpin =
      activeTab === "holdings"
        ? loading
        : activeTab === "closed"
          ? closedLoading
          : activeTab === "history"
            ? histLoading
            : watchLoading;
    return (
      <div className="user-holdings-tab-bar-extra-right">
        <div className="user-holdings-profit-metrics-row">
          <div className="user-holdings-profit-item" onClick={() => setDailyProfitModalOpen(true)}>
            <span className="user-holdings-profit-label">今日收益</span>
            <StockProfitRate value={todayProfit.amount} rate={todayProfit.rate} lines={1} />
          </div>
          <div className="user-holdings-profit-item" onClick={() => setClosedProfitModalOpen(true)}>
            <span className="user-holdings-profit-label">完结收益</span>
            <StockProfitRate value={closedProfit.amount} rate={closedProfit.rate} lines={1} />
            <Tooltip title="仅统计最近180天内的完结股票">
              <span className="user-holding-hint-trigger" onClick={(e) => e.stopPropagation()}>
                <QuestionCircleOutlined />
              </span>
            </Tooltip>
          </div>
          <div className="user-holdings-profit-item user-holdings-profit-item--readonly">
            <span className="user-holdings-profit-label">完结胜率</span>
            <span className="user-holdings-win-rate-value">
              {closedWinRatePct == null ? "—" : `${closedWinRatePct.toFixed(2)}%`}
            </span>
            <Tooltip title="仅统计最近180天内的完结股票">
              <span className="user-holding-hint-trigger">
                <QuestionCircleOutlined />
              </span>
            </Tooltip>
          </div>
        </div>
        <Tooltip title="重新加载数据">
          <span
            className="user-holdings-data-refresh user-holdings-data-refresh--nav-extra"
            role="button"
            tabIndex={0}
            onClick={() => reloadCurrentTabData()}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                reloadCurrentTabData();
              }
            }}
          >
            <ReloadOutlined spin={refreshSpin} className="user-holdings-data-refresh-icon" />
            <span className="user-holdings-data-refresh-text">更新数据</span>
          </span>
        </Tooltip>
      </div>
    );
  }, [
    activeTab,
    loading,
    closedLoading,
    histLoading,
    watchLoading,
    reloadCurrentTabData,
    todayProfit,
    closedProfit,
    closedWinRatePct,
  ]);

  // 转换曲线数据为CanvasCurveChart格式
  const dailyProfitCurveData: CurveDataPoint[] = useMemo(() => {
    return curve.map((p) => ({
      amount: p.dailyProfitAmount ?? 0,
      date: p.statDate ?? "",
    }));
  }, [curve]);

  const closedProfitCurveData: CurveDataPoint[] = useMemo(() => {
    return curve.map((p) => ({
      amount: p.closedProfitAmount ?? 0,
      date: p.statDate ?? "",
    }));
  }, [curve]);

  const pageSizeHist = 20;
  const histPageSlice = (hist ?? []).slice(
    (histPage - 1) * pageSizeHist,
    histPage * pageSizeHist
  );

  const historyCols: ColumnsType<AppUserTradeHistoryLineDTO> = [
    {
      title: "股票",
      align: "left",
      width: 100,
      render: (_, r) => {
        const code = String(r.stockCode ?? "").trim();
        const name = String(r.stockName ?? "—");
        if (!code) {
          return <span className="user-holding-tight">{name}</span>;
        }
        return (
          <StockLink
            stockCode={code}
            stockName={name}
            stockDetail={stockLinkDetail.code === code ? stockLinkDetail.data : null}
            detailLoading={stockLinkDetail.code === code && stockLinkDetail.loading}
            dayKlines={stockLinkDetail.code === code ? stockLinkDetail.dayKlines : null}
            klineLoading={stockLinkDetail.code === code && stockLinkDetail.klineLoading}
            onRequestDetail={requestStockDetailForLink}
            stockNotesTab={buildStockNotesTabProps(code, name)}
          />
        );
      },
    },
    { title: "操作类型", align: "center", dataIndex: "bizTypeDisplay", width: 80 },
    {
      title: "买入价格",
      align: "center",
      width: 80,
      render: (_: unknown, r) => <Amount value={r.unitPrice} />,
    },
    {
      title: "总金额/股数",
      align: "center",
      width: 80,
      render: (_, r) => (
        <div>
          <Amount value={r.grossAmount} />
          <div className="user-holding-sub">{r.shares}</div>
        </div>
      ),
    },
    { title: "交易时间", align: "center", dataIndex: "bizTime", width: 120, render: (t) => formatCnMin(t) },
    {
      title: "操作理由",
      align: "left",
      width: 220,
      onCell: () => ({ className: "user-holding-reason-td" }),
      render: (_: unknown, r) => <BuyReasonCell text={r.reasonDisplay} />,
    },
  ];

  // 已完结列表列定义
  const closedTradeColumns: ColumnsType<ClosedTradeLineDTO> = [
    {
      title: "股票",
      width: 100,
      render: (_: unknown, r) => (
        <div className="user-holding-stock-cell">
          <div className="user-holding-stock-name">{r.stockName}</div>
          <div className="user-holding-stock-code">{r.stockCode}</div>
        </div>
      ),
    },
    {
      title: "卖出股数",
      align: "center",
      width: 80,
      dataIndex: "sellShares",
    },
    {
      title: "卖出价格",
      align: "center",
      width: 80,
      render: (_: unknown, r) => <Amount value={r.sellPrice} />,
    },
    {
      title: "成本价",
      align: "center",
      width: 80,
      render: (_: unknown, r) => <Amount value={r.costPrice} />,
    },
    {
      title: "盈亏金额",
      align: "center",
      width: 100,
      render: (_: unknown, r) => {
        const v = r.profitAmount ?? 0;
        return (
          <span className={v >= 0 ? "user-holding-up" : "user-holding-down"}>
            <Amount value={v} />
          </span>
        );
      },
    },
    {
      title: "盈亏比例",
      align: "center",
      width: 100,
      render: (_: unknown, r) => {
        const cost = (r.costPrice ?? 0) * (r.sellShares ?? 0);
        const profit = r.profitAmount ?? 0;
        const rate = cost > 0 ? (profit / cost) * 100 : 0;
        return (
          <StockProfitRate
            value={profit}
            rate={rate}
            lines={1}
          />
        );
      },
    },
    {
      title: "卖出时间",
      align: "center",
      dataIndex: "sellTime",
      width: 140,
      render: (t) => formatCnMin(t),
    },
    {
      title: "卖出理由",
      align: "left",
      width: 220,
      onCell: () => ({ className: "user-holding-reason-td" }),
      render: (_: unknown, r) => <BuyReasonCell text={r.reasonDisplay} />,
    },
  ];

  const watchlistColumns: ColumnsType<UserWatchlistItemDTO> = useMemo(
    () => [
      {
        title: "股票",
        align: "left",
        width: 168,
        render: (_: unknown, r) => {
          const code = String(r.stockCode ?? "").trim();
          const name = String(r.stockName ?? "—");
          if (!code) {
            return <span className="user-holding-tight">{name}</span>;
          }
          return (
            <StockLink
              stockCode={code}
              stockName={name}
              stockDetail={stockLinkDetail.code === code ? stockLinkDetail.data : null}
              detailLoading={stockLinkDetail.code === code && stockLinkDetail.loading}
              dayKlines={stockLinkDetail.code === code ? stockLinkDetail.dayKlines : null}
              klineLoading={stockLinkDetail.code === code && stockLinkDetail.klineLoading}
              onRequestDetail={requestStockDetailForLink}
              stockNotesTab={buildStockNotesTabProps(code, name)}
            />
          );
        },
      },
      {
        title: "添加理由",
        align: "left",
        width: 200,
        onCell: () => ({ className: "user-holding-reason-td" }),
        render: (_: unknown, r) => (
          <Tooltip title={String(r.addReason ?? "").trim() || undefined}>
            <span className="user-holding-reason-ellipsis">
              {r.addReason != null && String(r.addReason).trim() !== "" ? String(r.addReason).trim() : "—"}
            </span>
          </Tooltip>
        ),
      },
      {
        title: "股票价格",
        align: "center",
        width: 112,
        render: (_: unknown, r) => <WatchlistRowPriceCell stockCode={String(r.stockCode ?? "")} />,
      },
      {
        title: "基本面分",
        align: "center",
        width: 88,
        render: (_: unknown, r) => <WatchlistFundamentalScoreCell stockCode={r.stockCode} />,
      },
      {
        title: "位置分",
        align: "center",
        width: 88,
        render: (_: unknown, r) => <WatchlistPositionScoreCell stockCode={r.stockCode} />,
      },
      {
        title: "操作",
        align: "center",
        fixed: "right",
        width: 168,
        render: (_: unknown, r) => (
          <>
            <Button
              type="link"
              size="small"
              onClick={() =>
                void (async () => {
                  const code = String(r.stockCode ?? "").trim();
                  if (!code) {
                    return;
                  }
                  const holding = await UserWatchlistApi.findOpenHoldingByStock(spaceId, code);
                  if (holding?.holdingId) {
                    setAddModal(holding);
                    return;
                  }
                  setOpenBuySeed({ id: 0, code, name: String(r.stockName ?? "").trim() || code });
                  setOpenBuyModal(true);
                })()
              }
            >
              建仓
            </Button>
            <Button
              type="link"
              size="small"
              onClick={() => {
                Modal.confirm({
                  title: "移出自选股",
                  content: `确定从分组「${watchGroups.find((g) => g.id === watchGroupId)?.groupName ?? ""}」中移除「${r.stockName ?? r.stockCode}」吗？`,
                  okText: "确认",
                  cancelText: "取消",
                  okButtonProps: { className: RECT_BTN, style: { width: 120 } },
                  cancelButtonProps: { className: RECT_BTN, style: { width: 120 } },
                  onOk: () =>
                    UserWatchlistApi.removeItem(spaceId, String(r.id ?? "")).then((ok) => {
                      if (ok) {
                        message.success("已移除");
                        void loadWatchItems();
                      } else {
                        message.error("移除失败");
                      }
                    }),
                });
              }}
            >
              移除
            </Button>
          </>
        ),
      },
    ],
    [
      stockLinkDetail,
      requestStockDetailForLink,
      buildStockNotesTabProps,
      spaceId,
      loadWatchItems,
      watchGroups,
      watchGroupId,
    ]
  );

  return (
    <div className="user-holdings-page" style={userHoldingsPageCssVars()}>
      <Tabs
        className="user-holdings-main-tabs"
        activeKey={activeTab}
        tabBarExtraContent={{ right: tabBarMetricsExtra }}
        onChange={(k) => {
          setActiveTab(k as "holdings" | "closed" | "history" | "watchlist");
          setHoldingsPage(1);
          setClosedPage(1);
          setHistPage(1);
        }}
        items={[
          {
            key: "holdings",
            label: "我的持仓",
            children: (
              <>
                <div className="user-holdings-tab-toolbar">
                  <Button
                    type="primary"
                    className={`${RECT_BTN} user-holdings-open-buy-btn`}
                    style={{ width: 120 }}
                    icon={<PlusOutlined />}
                    onClick={() => {
                      setOpenBuySeed(null);
                      setOpenBuyModal(true);
                    }}
                  >
                    建仓买入
                  </Button>
                </div>
                <Table
                  className="user-holdings-table-plain"
                  loading={loading}
                  bordered={false}
                  rowKey={(r) => r.holdingId ?? `${r.stockCode}-${r.buyTime}`}
                  dataSource={holdingsPageSlice}
                  pagination={{
                    pageSize: HOLDINGS_PAGE_SIZE,
                    total: openHoldingsRows.length,
                    current: holdingsPage,
                    onChange: (p) => setHoldingsPage(p),
                    showSizeChanger: false,
                  }}
                  columns={columns}
                  scroll={{ x: true }}
                />
              </>
            ),
          },
          {
            key: "closed",
            label: "已完结",
            children: (
              <>
                <Table
                  className="user-holdings-table-plain"
                  loading={closedLoading}
                  bordered={false}
                  rowKey={(r) => r.tradeId ?? `${r.stockCode}-${r.sellTime}`}
                  dataSource={closedTrades.slice((closedPage - 1) * HOLDINGS_PAGE_SIZE, closedPage * HOLDINGS_PAGE_SIZE)}
                  pagination={{
                    pageSize: HOLDINGS_PAGE_SIZE,
                    total: closedTrades.length,
                    current: closedPage,
                    onChange: (p) => setClosedPage(p),
                    showSizeChanger: false,
                  }}
                  columns={closedTradeColumns}
                  scroll={{ x: true }}
                />
              </>
            ),
          },
          {
            key: "history",
            label: "交易历史",
            children: (
              <>
                <div className="user-holdings-tab-toolbar">
                  <Button type="primary" className={RECT_BTN} style={{ width: 120 }} onClick={() => setAiOpen(true)}>
                    月度AI复盘
                  </Button>
                </div>
                <Table
                  className="user-holdings-table-plain"
                  loading={histLoading}
                  bordered={false}
                  columns={historyCols}
                  dataSource={histPageSlice}
                  pagination={{
                    pageSize: pageSizeHist,
                    total: (hist ?? []).length,
                    current: histPage,
                    onChange: (p) => setHistPage(p),
                  }}
                  rowKey={(_, idx) => `h-${idx}`}
                />
              </>
            ),
          },
          {
            key: "watchlist",
            label: "自选股",
            children: (
              <div className="uh-watchlist-layout">
                <div className="uh-watchlist-toolbar-row" aria-label="自选分组与操作">
                  <div className="uh-watchlist-toolbar-groups-grow">
                    <WatchlistGroupsNav
                      groups={watchGroups}
                      watchGroupId={watchGroupId}
                      onSelectGroup={setWatchGroupId}
                    />
                  </div>
                  <div className="uh-watchlist-toolbar-actions">
                    <Button
                      type="link"
                      className="uh-watchlist-toolbar-text-btn"
                      onClick={() => setWatchAddGroupOpen(true)}
                    >
                      添加分组
                    </Button>
                    <Button
                      type="link"
                      className="uh-watchlist-toolbar-text-btn"
                      style={{marginRight: 12}}
                      onClick={() => setWatchManageOpen(true)}
                    >
                      分组管理
                    </Button>
                   
                    <Button
                      type="primary"
                      className={`${RECT_BTN} uh-watchlist-add-stock-btn`}
                      style={{ width: 120 }}
                      icon={<PlusOutlined />}
                      onClick={() => setAddWatchOpen(true)}
                    >
                      添加自选
                    </Button>
                  </div>
                </div>
                <div className="uh-watchlist-main">
                  <Table
                    className="user-holdings-table-plain"
                    loading={watchLoading}
                    bordered={false}
                    rowKey={(r) => r.id ?? `${r.stockCode}-${r.createdAt}`}
                    dataSource={watchItems}
                    pagination={false}
                    columns={watchlistColumns}
                    scroll={{ x: true }}
                  />
                </div>
              </div>
            ),
          },
        ]}
      />

      <AddWatchlistItemModal
        spaceId={spaceId}
        open={addWatchOpen}
        groups={watchGroups}
        defaultSelectedGroupId={watchGroupId}
        onRefreshGroups={() => void loadWatchGroups()}
        onOpenNoteImport={noteImportHandler}
        onClose={(ok) => {
          setAddWatchOpen(false);
          if (ok) {
            void loadWatchItems();
          }
        }}
      />

      <WatchlistCreateGroupModal
        open={watchAddGroupOpen}
        spaceId={spaceId}
        onClose={() => setWatchAddGroupOpen(false)}
        onAfterListRefresh={() => void loadWatchGroups()}
      />

      <Modal
        title="分组管理"
        open={watchManageOpen}
        onCancel={() => setWatchManageOpen(false)}
        footer={null}
        width={480}
        destroyOnClose
        className="uh-watchlist-manage-modal"
      >
        {watchGroups.length === 0 ? (
          <div className="uh-watchlist-manage-empty">暂无分组</div>
        ) : (
          <ul className="uh-watchlist-manage-list">
            {watchGroups.map((g, idx) => {
              const gid = String(g.id ?? "");
              const gname = String(g.groupName ?? "—");
              return (
                <li key={g.id ?? `mg-${idx}`} className="uh-watchlist-manage-row">
                  <span className="uh-watchlist-manage-name" title={gname}>
                    {gname}
                  </span>
                  <Button
                    type="link"
                    danger
                    className="uh-watchlist-manage-del"
                    onClick={() => {
                      Modal.confirm({
                        title: "删除分组",
                        content: `确定删除分组「${gname}」及其中的自选股票吗？`,
                        okText: "确认",
                        cancelText: "取消",
                        okButtonProps: { className: RECT_BTN, style: { width: 120 } },
                        cancelButtonProps: { className: RECT_BTN, style: { width: 120 } },
                        onOk: () =>
                          UserWatchlistApi.deleteGroup(spaceId, gid).then((ok) => {
                            if (ok) {
                              message.success("已删除分组");
                              void loadWatchGroups();
                            } else {
                              message.error("删除失败");
                            }
                          }),
                      });
                    }}
                  >
                    删除
                  </Button>
                </li>
              );
            })}
          </ul>
        )}
      </Modal>

      <OpenBuyModal
        spaceId={spaceId}
        open={openBuyModal}
        seedStock={openBuySeed}
        onOpenNoteImport={noteImportHandler}
        onClose={(ok) => {
          setOpenBuyModal(false);
          setOpenBuySeed(null);
          if (ok) {
            void loadList();
          }
        }}
        holdingsCount={allHoldings.filter((r) => r.holdingStatus === "OPEN").length}
      />
      <AiReviewModal spaceId={spaceId} open={aiOpen} onClose={() => setAiOpen(false)} />
      <AddBuyModalWrap
        spaceId={spaceId}
        row={addModal}
        onOpenNoteImport={noteImportHandler}
        onClose={(ok) => {
          setAddModal(null);
          if (ok) void loadList();
        }}
      />
      <SellModalWrap
        spaceId={spaceId}
        row={sellModal}
        onOpenNoteImport={noteImportHandler}
        onClose={(ok) => {
          setSellModal(null);
          if (ok) void loadList();
        }}
      />

      <StockNoteImportPicker
        open={noteImport != null}
        stockCode={noteImport?.stockCode ?? ""}
        stockName={noteImport?.stockName}
        notes={noteImport ? listNotesForImport(noteImport.stockCode) : []}
        onClose={() => setNoteImport(null)}
        onPick={(note) => noteImport?.onPick(note.content)}
        onGoWriteNote={() => {
          if (!noteImport) {
            return;
          }
          const target = { code: noteImport.stockCode, name: noteImport.stockName };
          setNoteImport(null);
          setWriteNoteTarget(target);
          requestStockDetailForLink(target.code);
        }}
      />

      {writeNoteTarget ? (
        <StockLink
          hideTrigger
          modalOpen
          initialTabKey="notes"
          stockCode={writeNoteTarget.code}
          stockName={writeNoteTarget.name}
          stockDetail={
            stockLinkDetail.code === writeNoteTarget.code ? stockLinkDetail.data : null
          }
          detailLoading={
            stockLinkDetail.code === writeNoteTarget.code && stockLinkDetail.loading
          }
          dayKlines={
            stockLinkDetail.code === writeNoteTarget.code ? stockLinkDetail.dayKlines : null
          }
          klineLoading={
            stockLinkDetail.code === writeNoteTarget.code && stockLinkDetail.klineLoading
          }
          onRequestDetail={requestStockDetailForLink}
          stockNotesTab={buildStockNotesTabProps(writeNoteTarget.code, writeNoteTarget.name)}
          onModalOpenChange={(o) => {
            if (!o) {
              setWriteNoteTarget(null);
            }
          }}
        />
      ) : null}

      {/* 今日收益曲线弹框 */}
      <Modal
        open={dailyProfitModalOpen}
        title="日收益变化曲线"
        onCancel={() => setDailyProfitModalOpen(false)}
        footer={[
          <Button key="close" className={RECT_BTN} style={{ width: 120 }} onClick={() => setDailyProfitModalOpen(false)}>
            关闭
          </Button>,
        ]}
        width={860}
      >
        <div style={{ padding: "20px 0" }}>
          <CanvasCurveChart
            data={dailyProfitCurveData}
            width={800}
            height={320}
          />
        </div>
      </Modal>

      {/* 完结收益曲线弹框 */}
      <Modal
        open={closedProfitModalOpen}
        title="日完结收益变化曲线"
        onCancel={() => setClosedProfitModalOpen(false)}
        footer={[
          <Button key="close" className={RECT_BTN} style={{ width: 120 }} onClick={() => setClosedProfitModalOpen(false)}>
            关闭
          </Button>,
        ]}
        width={860}
      >
        <div style={{ padding: "20px 0" }}>
          <CanvasCurveChart
            data={closedProfitCurveData}
            width={800}
            height={320}
          />
        </div>
      </Modal>
    </div>
  );
}

/** 添加自选股：先选股，再选分组；分组列表末项为「添加分组」 */
function AddWatchlistItemModal(props: {
  spaceId: string;
  open: boolean;
  groups: UserWatchlistGroupDTO[];
  /** 打开弹窗时默认选中的分组（通常为自选 Tab 当前选中的分组） */
  defaultSelectedGroupId: string;
  onRefreshGroups: () => void | Promise<void>;
  onOpenNoteImport?: (stockCode: string, stockName: string, onPick: (content: string) => void) => void;
  onClose: (ok: boolean) => void;
}): JSX.Element {
  const { spaceId, open, groups, defaultSelectedGroupId, onRefreshGroups, onOpenNoteImport, onClose } = props;
  const [picked, setPicked] = useState<StockSearchBrief | null>(null);
  const [selectedGroupId, setSelectedGroupId] = useState("");
  const [reason, setReason] = useState("");
  const [newGroupModalOpen, setNewGroupModalOpen] = useState(false);
  const [refreshingGroups, setRefreshingGroups] = useState(false);
  const addWatchOpenWasOpenRef = useRef(false);

  useEffect(() => {
    const wasOpen = addWatchOpenWasOpenRef.current;
    addWatchOpenWasOpenRef.current = open;

    if (!open) {
      setNewGroupModalOpen(false);
      return;
    }

    // 仅在关闭→打开时重置表单并带入默认分组；弹窗打开期间 groups 刷新不改变已选分组
    if (!wasOpen) {
      setPicked(null);
      setReason("");
      setNewGroupModalOpen(false);
      const prefer = String(defaultSelectedGroupId ?? "").trim();
      const inList =
        prefer &&
        groups.some((g) => String(g.id ?? "") === prefer)
          ? prefer
          : "";
      setSelectedGroupId(inList);
    }
  }, [open, defaultSelectedGroupId, groups]);

  const handleRefreshGroups = async () => {
    setRefreshingGroups(true);
    try {
      await onRefreshGroups();
    } finally {
      setRefreshingGroups(false);
    }
  };

  const submit = async () => {
    const gid = String(selectedGroupId ?? "").trim();
    if (!gid) {
      message.error("请选择分组");
      throw new Error("cancel");
    }
    if (!picked?.code) {
      message.error("请选择标的");
      throw new Error("cancel");
    }
    const rs = reason.trim();
    if (!rs) {
      message.warning("请填写添加理由");
      throw new Error("cancel");
    }
    const ok = await UserWatchlistApi.addItem(spaceId, {
      groupId: gid,
      stockCode: picked.code,
      stockName: picked.name,
      addReason: rs.slice(0, 50),
    });
    if (ok) {
      message.success("已加入自选");
      onClose(true);
    } else {
      message.error("添加失败（可能已在该分组中）");
      throw new Error("cancel");
    }
  };

  const groupSelectOptions = useMemo(
    () => [
      ...groups.map((g) => ({
        label: g.groupName ?? "—",
        value: String(g.id ?? ""),
      })),
      {
        label: (
          <span className="uh-watchlist-add-group-option">
            <PlusOutlined />
            <span>添加分组</span>
          </span>
        ),
        value: ADD_WATCHLIST_GROUP_VALUE,
      },
    ],
    [groups]
  );

  return (
    <>
      <Modal
        open={open}
        title="添加自选"
        onCancel={() => onClose(false)}
        width={620}
        okText="确认"
        cancelText="取消"
        okButtonProps={{ className: RECT_BTN, style: { width: 120 } }}
        cancelButtonProps={{ className: RECT_BTN, style: { width: 120 } }}
        onOk={() => submit()}
        className="uh-add-watch-modal"
        destroyOnClose
      >
        <div className="user-holding-form-block">
          <SearchStockWithCatalog onSelect={(s) => setPicked(s)} />
        </div>
        <div className="user-holding-form-block" style={{ marginTop: 16 }}>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 8,
              width: "100%",
            }}
          >
            <Select
              className="user-holding-input uh-watchlist-modal-group-select"
              placeholder="请选择分组"
              options={groupSelectOptions}
              value={selectedGroupId || undefined}
              onChange={(v) => {
                const s = String(v ?? "");
                if (s === ADD_WATCHLIST_GROUP_VALUE) {
                  setNewGroupModalOpen(true);
                  return;
                }
                setSelectedGroupId(s);
              }}
              allowClear={false}
              style={{ flex: 1, minWidth: 0 }}
              popupClassName="uh-add-watch-modal-select-dropdown"
            />
            <Button
              type="default"
              className={RECT_BTN}
              style={{ width: 40, height: 32, padding: 0, flexShrink: 0 }}
              icon={<ReloadOutlined />}
              loading={refreshingGroups}
              aria-label="刷新分组列表"
              onClick={() => void handleRefreshGroups()}
            />
          </div>
        </div>
        <div className="user-holding-form-block" style={{ marginTop: 16 }}>
          <div className="uh-reason-detail-head">
            <span className="user-holding-lab">添加理由</span>
            {picked?.code && onOpenNoteImport ? (
              <button
                type="button"
                className="uh-reason-import-btn"
                onClick={() =>
                  onOpenNoteImport(picked.code, picked.name ?? "", (content) =>
                    setReason(content.slice(0, 50))
                  )
                }
              >
                从笔记中导入
              </button>
            ) : null}
          </div>
          <div className="uh-watchlist-reason-field">
            <Input.TextArea
              placeholder="添加理由，最多50个字"
              maxLength={50}
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              rows={3}
              className="user-holding-input uh-watchlist-reason-textarea"
            />
            <span className="uh-watchlist-reason-count">
              {reason.length}/50
            </span>
          </div>
        </div>
      </Modal>

      <WatchlistCreateGroupModal
        open={newGroupModalOpen}
        spaceId={spaceId}
        onClose={() => setNewGroupModalOpen(false)}
        onCreated={(id) => setSelectedGroupId(id)}
        onAfterListRefresh={onRefreshGroups}
      />
    </>
  );
}

/** 建仓 */
function OpenBuyModal(props: {
  spaceId: string;
  open: boolean;
  seedStock?: StockSearchBrief | null;
  onOpenNoteImport?: (stockCode: string, stockName: string, onPick: (content: string) => void) => void;
  onClose: (ok: boolean) => void;
  holdingsCount: number;
}): JSX.Element {
  const { spaceId, open, seedStock, onOpenNoteImport, onClose, holdingsCount } = props;
  const [picked, setPicked] = useState<StockSearchBrief | null>(null);
  const [buyPrice, setBuyPrice] = useState<number>();
  const [buyShares, setBuyShares] = useState<number>();
  const [reasonType, setReasonType] = useState(REASON_OPTS[2]);
  const [reasonDetail, setReasonDetail] = useState("");
  const [buyTime, setBuyTime] = useState<Dayjs | null>(dayjs());

  useEffect(() => {
    if (!open) {
      return;
    }
    setBuyTime(dayjs());
    setBuyPrice(undefined);
    setBuyShares(undefined);
    setReasonDetail("");
    setReasonType(REASON_OPTS[2]);
    if (seedStock?.code) {
      setPicked(seedStock);
    } else {
      setPicked(null);
    }
  }, [open, seedStock]);

  const ok = async () => {
    // 检查持仓数量限制
    if (holdingsCount >= 50) {
      message.warning("最多只允许添加50只持仓中的股票哦！您可以先卖出后再添加新持仓");
      return;
    }
    if (!picked?.code) {
      message.error("请选择标的");
      return;
    }
    if (!buyShares || !(buyPrice && buyPrice > 0)) {
      message.error("请填写成交价与股数");
      return;
    }
    const bt = (buyTime ?? dayjs()).format("YYYY-MM-DD HH:mm:ss");
    const err = await UserHoldingsApi.openBuyTry(spaceId, {
      stockCode: picked.code,
      stockName: picked.name,
      buyPrice,
      buyShares,
      buyTime: bt,
      reasonType,
      reasonDetail,
    });
    if (!err) {
      message.success("已建仓");
      onClose(true);
      return;
    }
    if (String(err).includes("已在持仓中")) {
      message.warning("该股票已在持仓中，如需加仓请重试");
      return;
    }
    message.error(err);
  };

  return (
    <Modal
      open={open}
      title="建仓买入"
      onCancel={() => onClose(false)}
      width={620}
      okText="确认"
      cancelText="取消"
      okButtonProps={{ className: RECT_BTN, style: { width: 120 } }}
      cancelButtonProps={{ className: RECT_BTN, style: { width: 120 } }}
      onOk={() => void ok()}
    >
      <div className="user-holding-form-block">
        <SearchStockWithCatalog onSelect={(s) => setPicked(s)} />
      </div>
      <div style={{ display: "flex", gap: 24, marginTop: 16 }}>
        <div className="user-holding-form-block" style={{ flex: 1 }}>
          <span className="user-holding-lab">成交价</span>
          <InputNumber className="user-holding-input" placeholder="成交价" style={{ width: "100%" }} value={buyPrice} onChange={(v) => setBuyPrice(typeof v === "number" ? v : undefined)} min={0} step={0.01} controls={false} />
        </div>
        <div className="user-holding-form-block" style={{ flex: 1 }}>
          <span className="user-holding-lab">股数</span>
          <InputNumber className="user-holding-input" placeholder="股数" style={{ width: "100%" }} value={buyShares} onChange={(v) => setBuyShares(typeof v === "number" ? v : undefined)} min={1} step={1} controls={false} />
        </div>
      </div>
      <div className="user-holding-form-block" style={{ marginTop: 12 }}>
        <DatePicker
          className="user-holding-input user-holding-datepicker"
          showTime
          format="YYYY-MM-DD HH:mm:ss"
          style={{ width: "100%" }}
          value={buyTime}
          onChange={(d) => setBuyTime(d)}
          placeholder="买入时间，默认当前时刻，可改为历史成交时间"
          allowClear={false}
        />
      </div>
      <div className="user-holding-form-block" style={{ marginTop: 12 }}>
        <span className="user-holding-lab">理由类别</span>
        <Select
          placeholder="请选择"
          options={REASON_OPTS.map((x) => ({ label: x, value: x }))}
          value={reasonType}
          onChange={setReasonType}
          style={{ width: "100%" }}
          allowClear={false}
        />
      </div>
      <ReasonDetailWithImport
        label="具体原因"
        placeholder="最多300字"
        value={reasonDetail}
        onChange={setReasonDetail}
        stockCode={picked?.code}
        onImportClick={
          picked?.code && onOpenNoteImport
            ? () =>
                onOpenNoteImport(picked.code, picked.name ?? "", (content) =>
                  setReasonDetail(content.slice(0, 300))
                )
            : undefined
        }
      />
    </Modal>
  );
}

/** 复盘大弹框 */
function AiReviewModal(props: {
  spaceId: string;
  open: boolean;
  onClose: () => void;
}): JSX.Element {
  const { spaceId, open, onClose } = props;
  const [detail, setDetail] = useState<Awaited<ReturnType<typeof UserHoldingsApi.latestReview>>>(null);

  useEffect(() => {
    if (open) {
      void UserHoldingsApi.latestReview(spaceId).then(setDetail);
    }
  }, [open, spaceId]);

  const [histOpen, setHistOpen] = useState(false);

  return (
    <>
      <Modal
        title={null}
        open={open}
        onCancel={() => onClose()}
        width={680}
        centered
        footer={[
          <Button key="his" type="primary" ghost className={RECT_BTN} style={{ width: 120 }} onClick={() => setHistOpen(true)}>
            历史复盘
          </Button>,
          <Button key="clo" type="primary" className={RECT_BTN} style={{ width: 120 }} onClick={() => onClose()}>
            关闭
          </Button>,
        ]}
        className="user-holding-ai-root uh-ai-modal-shell"
      >
        <div className="user-holding-ai-body uh-ai-modal-body">
          {!detail?.id ? (
            <div className="uh-ai-modal-loading-fill">
              <p className="uh-ai-muted">加载中…</p>
            </div>
          ) : (
            <MonthlyReviewDetailView detail={detail} />
          )}
        </div>
      </Modal>
      <HistoryReviewModal spaceId={spaceId} open={histOpen} onClose={() => setHistOpen(false)} />
    </>
  );
}

function HistoryReviewModal(props: {
  spaceId: string;
  open: boolean;
  onClose: () => void;
}): JSX.Element {
  const { spaceId, open, onClose } = props;
  const [list, setList] = useState<{ id?: string; title?: string; summary?: string }[]>([]);
  const [det, setDet] = useState<Awaited<ReturnType<typeof UserHoldingsApi.reviewDetail>>>(null);
  useEffect(() => {
    if (open) {
      void UserHoldingsApi.listReviews(spaceId).then((r) => setList(r?.rows ?? []));
    }
  }, [open, spaceId]);
  return (
    <Modal
      open={open}
      title={null}
      onCancel={() => onClose()}
      footer={null}
      width={400}
      className="uh-history-list-modal"
    >
      <p className="uh-ai-kicker" style={{ marginBottom: 8 }}>
        历史
      </p>
      <h3 className="uh-history-list-title">选择一期复盘</h3>
      <div className="uh-history-list">
        {list.map((x) => (
          <button
            type="button"
            key={x.id}
            className="uh-history-row"
            onClick={() => void UserHoldingsApi.reviewDetail(spaceId, String(x.id)).then(setDet)}
          >
            <span className="uh-history-row-title">{x.title}</span>
            {x.summary ? <span className="uh-history-row-sum">{x.summary}</span> : null}
          </button>
        ))}
      </div>
      <Modal
        title={null}
        open={!!det?.id}
        onCancel={() => setDet(null)}
        footer={null}
        width={680}
        centered
        destroyOnClose
        className="user-holding-ai-root uh-ai-modal-shell"
      >
        <div className="user-holding-ai-body uh-ai-modal-body">
          {det?.id ? <MonthlyReviewDetailView detail={det} /> : null}
        </div>
      </Modal>
    </Modal>
  );
}

function AddBuyModalWrap(props: {
  spaceId: string;
  row: AppUserHoldingLineDTO | null;
  onOpenNoteImport?: (stockCode: string, stockName: string, onPick: (content: string) => void) => void;
  onClose: (ok: boolean) => void;
}): JSX.Element | null {
  const r = props.row;
  const [buyPrice, setBuyPrice] = useState<number>();
  const [buyShares, setBuyShares] = useState<number>();
  const [reasonType, setReasonType] = useState(REASON_OPTS[2]);
  const [reasonDetail, setReasonDetail] = useState("");
  const [buyTime, setBuyTime] = useState<Dayjs | null>(dayjs());

  useEffect(() => {
    if (r?.holdingId) {
      setBuyTime(dayjs());
    }
  }, [r?.holdingId]);

  if (!r) {
    return null;
  }

  const submit = async () => {
    if (!buyShares || !(buyPrice && buyPrice > 0)) return;
    const ok = await UserHoldingsApi.addBuy(props.spaceId, {
      stockCode: r.stockCode,
      stockName: r.stockName,
      buyPrice,
      buyShares,
      buyTime: (buyTime ?? dayjs()).format("YYYY-MM-DD HH:mm:ss"),
      reasonType,
      reasonDetail,
    });
    if (ok) {
      message.success("已加仓");
      props.onClose(true);
    }
  };

  return (
    <Modal
      title={`加仓 · ${r.stockName}`}
      open
      okText="确认"
      cancelText="取消"
      okButtonProps={{ className: RECT_BTN, style: { width: 120 } }}
      cancelButtonProps={{ className: RECT_BTN, style: { width: 120 } }}
      onOk={() => void submit()}
      onCancel={() => props.onClose(false)}
    >
      <div className="user-holding-form-block">
        <span className="user-holding-lab">成交价</span>
        <InputNumber placeholder="成交价" style={{ width: "100%" }} value={buyPrice} onChange={(v) => setBuyPrice(typeof v === "number" ? v : undefined)} controls={false} />
      </div>
      <div className="user-holding-form-block" style={{ marginTop: 12 }}>
        <DatePicker
          className="user-holding-input user-holding-datepicker"
          showTime
          format="YYYY-MM-DD HH:mm:ss"
          style={{ width: "100%" }}
          value={buyTime}
          onChange={(d) => setBuyTime(d)}
          placeholder="成交时间，默认当前时刻，可改为历史时间"
          allowClear={false}
        />
      </div>
      <div className="user-holding-form-block" style={{ marginTop: 12 }}>
        <span className="user-holding-lab">买入股数</span>
        <InputNumber placeholder="股数" style={{ width: "100%" }} value={buyShares} min={1} onChange={(v) => setBuyShares(typeof v === "number" ? v : undefined)} controls={false} />
      </div>
      <div className="user-holding-form-block" style={{ marginTop: 12 }}>
        <span className="user-holding-lab">原因类型</span>
        <Select
          placeholder="请选择"
          options={REASON_OPTS.map((x) => ({ label: x, value: x }))}
          value={reasonType}
          onChange={setReasonType}
          style={{ width: "100%" }}
        />
      </div>
      <ReasonDetailWithImport
        label="具体原因（最多300字）"
        placeholder="请输入"
        value={reasonDetail}
        onChange={setReasonDetail}
        stockCode={r.stockCode}
        onImportClick={
          r.stockCode && props.onOpenNoteImport
            ? () =>
                props.onOpenNoteImport?.(String(r.stockCode), String(r.stockName ?? ""), (content) =>
                  setReasonDetail(content.slice(0, 300))
                )
            : undefined
        }
      />
    </Modal>
  );
}

function SellModalWrap(props: {
  spaceId: string;
  row: AppUserHoldingLineDTO | null;
  onOpenNoteImport?: (stockCode: string, stockName: string, onPick: (content: string) => void) => void;
  onClose: (ok: boolean) => void;
}): JSX.Element | null {
  const r = props.row;
  const [sellPrice, setSellPrice] = useState<number>();
  const [sellShares, setSellShares] = useState<number>(
    r?.buyShares && r.buyShares > 0 ? r.buyShares : 0
  );
  const [sellTime, setSellTime] = useState(dayjs().format("YYYY-MM-DD HH:mm:ss"));
  const [reasonType, setReasonType] = useState(REASON_OPTS[0]);
  const [reasonDetail, setReasonDetail] = useState("");

  React.useEffect(() => {
    if (r?.buyShares != null && r.buyShares > 0) {
      setSellShares(r.buyShares);
    }
  }, [r?.buyShares, r]);

  if (!r || !r.holdingId) {
    return null;
  }

  const pct =
    typeof r.positionProfitRatio === "number"
      ? r.positionProfitRatio
      : typeof r.buyPrice === "number" &&
          typeof r.spotPriceSuggest === "number" &&
          r.buyPrice > 0
        ? (((r.spotPriceSuggest ?? 0) - r.buyPrice) / r.buyPrice) * 100
        : 0;

  const submitSell = async () => {
    if (!sellShares || !(sellPrice && sellPrice > 0)) return;
    const ok = await UserHoldingsApi.sell(props.spaceId, {
      holdingId: r.holdingId,
      sellPrice,
      sellShares,
      sellTime,
      reasonType,
      reasonDetail,
    });
    if (ok) {
      message.success("已卖出");
      props.onClose(true);
    }
  };

  return (
    <Modal
      title={`卖出 · ${r.stockName ?? ""}`}
      open
      okText="确认"
      cancelText="取消"
      okButtonProps={{ className: RECT_BTN, style: { width: 120 } }}
      cancelButtonProps={{ className: RECT_BTN, style: { width: 120 } }}
      width={560}
      onOk={() => void submitSell()}
      onCancel={() => props.onClose(false)}
    >
      <div style={{ marginBottom: 8 }}>
        <StockPrice mode="two-line" price={r.spotPriceSuggest ?? r.buyPrice ?? 0} change={pct} />
      </div>
      <div style={{ marginBottom: 8 }}>
        买入均价 <Text>{String(r.buyPrice ?? "—")}</Text>
      </div>
      <div className="user-holding-form-block">
        <span className="user-holding-lab">卖出价格</span>
        <InputNumber placeholder="成交价" style={{ width: "100%" }} value={sellPrice} onChange={(v) => setSellPrice(typeof v === "number" ? v : undefined)} controls={false} />
      </div>
      <div className="user-holding-form-block" style={{ marginTop: 12, position: "relative" }}>
        <button type="button" className="user-holding-qingcang" onClick={() => setSellShares(r.buyShares ?? sellShares)}>
          清仓
        </button>
        <span className="user-holding-lab">卖出股数</span>
        <InputNumber
          placeholder="股数"
          style={{ width: "100%" }}
          value={sellShares}
          min={1}
          max={r.buyShares}
          onChange={(v) => setSellShares(typeof v === "number" ? v : r.buyShares ?? 1)}
          controls={false}
        />
      </div>
      <div className="user-holding-form-block" style={{ marginTop: 12 }}>
        <span className="user-holding-lab">卖出时间</span>
        <Input value={sellTime} onChange={(e) => setSellTime(e.target.value)} placeholder="YYYY-MM-DD HH:mm:ss" />
      </div>
      <div className="user-holding-form-block" style={{ marginTop: 12 }}>
        <span className="user-holding-lab">卖出理由类型</span>
        <Select options={REASON_OPTS.map((x) => ({ label: x, value: x }))} value={reasonType} onChange={setReasonType} style={{ width: "100%" }} />
      </div>
      <ReasonDetailWithImport
        label="具体原因"
        value={reasonDetail}
        onChange={setReasonDetail}
        stockCode={r.stockCode}
        onImportClick={
          r.stockCode && props.onOpenNoteImport
            ? () =>
                props.onOpenNoteImport?.(String(r.stockCode), String(r.stockName ?? ""), (content) =>
                  setReasonDetail(content.slice(0, 300))
                )
            : undefined
        }
      />
    </Modal>
  );
}
